import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import {
  validateUserEmail,
  validateUserPassword,
  validateUserRepeatPassword,
} from '../../services/validation/user-validation';
import TextInput from '@common/components/Inputs/TextInput';
import { EmailIcon } from '@common/utils/icons/icons';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import { LoginVariant } from '../../models/general/login-details';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { login, postResetPasswordEmail, setPassword } from '../../store/general/general-actions';
import Collapse from '@mui/material/Collapse';
import { selectUser } from '../../store/general/general-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { useSearchParams } from 'react-router-dom';
import { isHttpError } from '@common/interfaces/api/http-error';
import FormHelperText from '@mui/material/FormHelperText';
import Link from '@mui/material/Link';
import { PRIVACY_POLICY_URL } from 'utils/cookieConsent';
import { getPasswordStrength } from '../../utils/passwordStrength';
import PasswordInput from 'components/forms/PasswordInput';

const LocalLogin: React.FC = () => {
  const { t } = useTranslation();

  const [filterParams, setFilterParams] = useSearchParams();
  const queryToken = filterParams.get('token');
  const queryUserId = filterParams.get('uid');

  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => selectUser(state));

  const [isForgotPasswordActive, setIsForgotPasswordActive] = useState(false);

  const isSetPasswordMode =
    queryToken != null && queryToken.length > 0 && queryUserId != null && queryUserId.length > 0;

  const { resetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      postError: isSetPasswordMode
        ? user.rest.postSetPasswordError
        : isForgotPasswordActive
          ? user.rest.postResetPasswordEmailError
          : user.rest.postError,
    });

  const {
    value: email,
    validationResult: emailValidationResult,
    valueChangeHandler: emailChangeHandler,
  } = useValidatedInputEffect(
    '',
    (newEmail) => validateUserEmail(newEmail),
    (_) => () => {},
    getServerValidationResultOrNull('email'),
    removeServerError,
    resetToggle,
  );

  const {
    value: password,
    validationResult: passwordValidationResult,
    valueChangeHandler: passwordChangeHandler,
  } = useValidatedInputEffect(
    '',
    (newPassword) => validateUserPassword(newPassword),
    (_) => () => {},
    getServerValidationResultOrNull('password'),
    removeServerError,
    resetToggle,
  );

  const {
    value: repeatPassword,
    validationResult: repeatPasswordValidationResult,
    valueChangeHandler: repeatPasswordChangeHandler,
  } = useValidatedInputEffect(
    '',
    (newPassword) => validateUserRepeatPassword(newPassword, password),
    (_) => () => {},
    getServerValidationResultOrNull('password'),
    removeServerError,
    resetToggle,
  );

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    if (isSetPasswordMode) {
      dispatch(setPassword({ token: queryToken, uid: queryUserId, password }));
    } else if (isForgotPasswordActive) {
      dispatch(postResetPasswordEmail(email));
    } else {
      dispatch(login({ email, password, variant: LoginVariant.Local }));
    }
  };

  const forgotPasswordClickHandler = () => {
    setIsForgotPasswordActive(true);
  };

  const backToLoginClickHandler = () => {
    setIsForgotPasswordActive(false);
  };

  const cancelSetPasswordClickHandler = () => {
    setFilterParams((prevState) => {
      prevState.delete('token');
      prevState.delete('uid');
      return prevState;
    });
  };

  const openForgotPasswordFormClickHandler = () => {
    cancelSetPasswordClickHandler();
    forgotPasswordClickHandler();
  };

  return (
    <Stack width="100%">
      {isForgotPasswordActive &&
        user.rest.postResetPasswordEmailStatus === HttpFetchStatus.Success && (
          <CustomAlert
            severity="success"
            header={t('success-password-reset-request')}
            sx={{ mb: 2 }}
          />
        )}
      {isSetPasswordMode &&
        user.rest.postSetPasswordError != null &&
        isHttpError(user.rest.postSetPasswordError) && (
          <CustomAlert
            severity="error"
            textMessages={[t(user.rest.postSetPasswordError.message)]}
            header={t('error-resetting-password')}
            sx={{ mb: 2 }}
            action={
              <Button sx={{ textWrap: 'nowrap' }} onClick={openForgotPasswordFormClickHandler}>
                {t('button-request-new-token')}
              </Button>
            }
          />
        )}
      <form onSubmit={formSubmitHandler}>
        <Stack spacing={2} pt={1}>
          <Collapse in={!isSetPasswordMode}>
            <TextInput
              label={t('input-label-user-email')}
              type="email"
              value={email}
              focused
              startIcon={<EmailIcon />}
              onChange={emailChangeHandler}
              validationResult={emailValidationResult}
            />
          </Collapse>
          <Collapse in={!isForgotPasswordActive}>
            <PasswordInput
              name="password"
              isSetField={isSetPasswordMode}
              password={password}
              onChange={passwordChangeHandler}
              label={t('input-label-user-password')}
              validationResult={passwordValidationResult}
            />
          </Collapse>
          <Collapse in={isSetPasswordMode}>
            <PasswordInput
              name="repeatPassword"
              isSetField={isSetPasswordMode}
              password={repeatPassword}
              onChange={repeatPasswordChangeHandler}
              label={t('input-label-user-repeat-password')}
              validationResult={repeatPasswordValidationResult}
            />
          </Collapse>
          <Stack spacing={1}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              type="submit"
              disabled={
                isSetPasswordMode &&
                (password !== repeatPassword || !getPasswordStrength(password).isValid)
              }
            >
              {isSetPasswordMode
                ? t('button-save-password')
                : isForgotPasswordActive
                  ? t('button-send-reset-password-email')
                  : t('button-login')}
            </Button>
            <Stack direction="row" justifyContent="space-between">
              <FormHelperText>
                {t('info-for-more-info-check-privacy-policy')}{' '}
                <Link href={PRIVACY_POLICY_URL} target="_blank" rel="noopener">
                  {t('link-privacy-policy')}
                </Link>
              </FormHelperText>
              <Button
                variant="text"
                onClick={
                  isSetPasswordMode
                    ? cancelSetPasswordClickHandler
                    : isForgotPasswordActive
                      ? backToLoginClickHandler
                      : forgotPasswordClickHandler
                }
              >
                {isSetPasswordMode
                  ? t('button-cancel')
                  : isForgotPasswordActive
                    ? t('button-back-to-login')
                    : t('button-forgot-password')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
};

export default LocalLogin;
